import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileSpreadsheet, CheckCircle2, AlertTriangle, Hourglass } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  MATCHED: { label: "Matched", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  INVOICE_PENDING: { label: "Invoice Pending", cls: "bg-amber-50 text-amber-800 border-amber-200", icon: Hourglass },
  QTY_MISMATCH: { label: "Qty Mismatch", cls: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
  AMOUNT_MISMATCH: { label: "Amount Mismatch", cls: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
  QTY_AND_AMOUNT_MISMATCH: { label: "Qty + Amount", cls: "bg-red-100 text-red-800 border-red-300", icon: AlertTriangle },
};

const CCY_SYM = { INR: "₹", USD: "$", EUR: "€", CNY: "¥", JPY: "¥" };
const fmtMoney = (n, ccy = "INR") => {
  if (n === null || n === undefined || n === "") return "—";
  const sym = CCY_SYM[ccy] || "";
  const locale = ccy === "INR" ? "en-IN" : "en-US";
  return `${sym}${Number(n).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function ThreeWayMatchReport() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [fy, setFy] = useState("");
  const [dlBusy, setDlBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status_filter = statusFilter;
      if (fy) params.fy = fy;
      const { data } = await api.get("/reports/three-way-match", { params });
      setRows(data.rows || []);
      setCounts(data.counts_by_status || {});
    } catch (e) {
      // 403 for non-financial roles — silently render nothing
      if (e.response?.status !== 403) toast.error("Failed to load 3-way match report");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, fy]);

  const downloadXlsx = async () => {
    setDlBusy(true);
    try {
      const token = localStorage.getItem("assetiq_token");
      const qs = new URLSearchParams();
      if (statusFilter) qs.set("status_filter", statusFilter);
      if (fy) qs.set("fy", fy);
      const res = await fetch(`${API_BASE}/reports/three-way-match.xlsx?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `3WayMatch_${fy || "All"}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally { setDlBusy(false); }
  };

  const total = rows.length;
  const kpis = useMemo(() => ([
    { key: "MATCHED", label: "Matched" },
    { key: "INVOICE_PENDING", label: "Invoice Pending" },
    { key: "AMOUNT_MISMATCH", label: "Amount Mismatch" },
    { key: "QTY_MISMATCH", label: "Qty Mismatch" },
    { key: "QTY_AND_AMOUNT_MISMATCH", label: "Qty + Amount" },
  ]), []);

  return (
    <Card data-testid="three-way-match-card">
      <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-heading text-base">3-Way Match — PO ↔ Receipt ↔ Invoice</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Compares raised PO against goods received and supplier invoice amount. Tolerance: 0.5% or ₹10.</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={downloadXlsx}
          disabled={dlBusy || rows.length === 0}
          data-testid="download-3way-match-btn"
        >
          {dlBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1.5" />}
          Export Excel
        </Button>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
          {kpis.map((k) => (
            <button
              key={k.key}
              onClick={() => setStatusFilter(statusFilter === k.key ? "" : k.key)}
              className={`text-left border rounded-md px-3 py-2 hover:border-[#7C1D3A] transition ${statusFilter === k.key ? "border-[#7C1D3A] bg-[#fcf0f3]" : "border-slate-200 bg-white"}`}
              data-testid={`3way-kpi-${k.key}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{k.label}</div>
              <div className="font-heading font-semibold text-lg text-slate-900">{counts[k.key] || 0}</div>
            </button>
          ))}
        </div>

        {/* filters */}
        <div className="flex flex-wrap gap-3 items-center text-xs">
          <label className="text-slate-500">FY:</label>
          <select
            value={fy}
            onChange={(e) => setFy(e.target.value)}
            className="border border-slate-200 rounded-md px-2 py-1"
            data-testid="3way-fy-select"
          >
            <option value="">All FYs</option>
            {["23-24", "24-25", "25-26", "26-27", "27-28"].map((f) => <option key={f} value={f}>FY {f}</option>)}
          </select>
          {(statusFilter || fy) && (
            <button
              onClick={() => { setStatusFilter(""); setFy(""); }}
              className="text-[#7C1D3A] hover:underline"
              data-testid="3way-clear-filters"
            >
              Clear filters
            </button>
          )}
          <span className="ml-auto text-slate-500">{total} PO(s)</span>
        </div>

        {/* table */}
        {loading ? (
          <div className="text-slate-400 text-center py-6 text-sm">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="text-slate-400 text-center py-8 text-sm border border-dashed border-slate-200 rounded-md">
            No POs to compare. Only POs at &quot;Received&quot; status or later are included.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-4">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-y border-slate-200 text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">PO#</th>
                  <th className="px-3 py-2 text-left">Vendor</th>
                  <th className="px-3 py-2 text-right">PO Qty</th>
                  <th className="px-3 py-2 text-right">Recv Qty</th>
                  <th className="px-3 py-2 text-right">PO Amount</th>
                  <th className="px-3 py-2 text-left">Invoice #</th>
                  <th className="px-3 py-2 text-right">Invoice Amt</th>
                  <th className="px-3 py-2 text-right">Variance</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const meta = STATUS_META[r.match_status] || STATUS_META.INVOICE_PENDING;
                  const Icon = meta.icon;
                  return (
                    <tr
                      key={r.po_id}
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/purchase-orders/${r.po_id}`)}
                      data-testid={`3way-row-${r.po_number?.replace(/\//g, "_")}`}
                    >
                      <td className="px-3 py-2 font-mono text-[#7C1D3A]">{r.po_number}</td>
                      <td className="px-3 py-2 text-slate-700">{r.vendor_name}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.po_qty}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.received_qty}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.po_amount, r.currency)}</td>
                      <td className="px-3 py-2 text-slate-600">{r.supplier_invoice_no || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.supplier_invoice_amount, r.currency)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${r.amount_variance && Math.abs(r.amount_variance) > 0.1 ? "text-red-700 font-semibold" : "text-slate-500"}`}>
                        {r.amount_variance === null ? "—" : fmtMoney(r.amount_variance, r.currency)}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`${meta.cls} font-normal text-[10px]`}>
                          <Icon className="w-3 h-3 mr-1 inline" />
                          {meta.label}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
